import { Stack, Tabs } from '@fti/design-system';
import { ReflectionFieldCard } from './ReflectionFieldCard';
import type { StaticReflectionNotes } from '../../../types/media-sim';

interface ReflectionCategoryTabsProps {
  notes: StaticReflectionNotes;
}

interface FieldDef {
  key: string;
  label: string;
  description: string;
}

const CATEGORIES: {
  value: string;
  label: string;
  noteKey: keyof StaticReflectionNotes;
  fields: FieldDef[];
}[] = [
  {
    value: 'newsworthiness',
    label: 'Newsworthiness',
    noteKey: 'newsworthiness',
    fields: [
      { key: 'minimum_scale_for_coverage', label: '(A) Minimum Scale For Coverage', description: 'What type and scale of impact is present to suggest why outlet covered this story? (geographic, human, financial)' },
      { key: 'elevation_rules', label: '(B) Elevation Rules', description: 'What events or actor involvement is seen to justify coverage? (lawsuits, government action, famous person, fatalities)' },
      { key: 'breaking_news', label: '(C) Breaking News', description: 'What does outlet consider to be breaking news?' },
      { key: 'frequently_covered_events_subjects_topics', label: '(D) Frequently Covered Events, Subjects, & Topics', description: 'What events, subjects, or topics does this outlet typically cover?' },
      { key: 'novelty_v_repetition', label: '(E) Novelty v Repetition', description: 'Does outlet tend to cover new events or perspectives or prioritize reliable and expected coverage of normal course events?' },
    ],
  },
  {
    value: 'editorial_angle',
    label: 'Editorial Angle',
    noteKey: 'editorial_angle_selection',
    fields: [
      { key: 'interpretive_lens', label: '(A) Interpretive Lens', description: 'What interpretive lens or story angle does outlet use in their coverage?' },
      { key: 'priority_stakeholders', label: '(B) Priority Stakeholders', description: 'What stakeholder perspectives (business leaders, politicians, etc.) does the outlet prioritize in their coverage?' },
      { key: 'downplayed_story_angles', label: '(C) Downplayed Story Angles', description: 'What story angles does outlet downplay in their coverage?' },
      { key: 'skeptical_claims', label: '(D) Skeptical Claims', description: 'What types of claims does outlet treat skeptically, introduce with caveats, or deprioritize?' },
      { key: 'accepted_facts', label: '(E) Accepted Facts', description: 'What types of facts does outlet state declaratively or accept at face value?' },
    ],
  },
  {
    value: 'story_architecture',
    label: 'Story Architecture',
    noteKey: 'story_architecture',
    fields: [
      { key: 'article_structure', label: '(A) Article Structure', description: 'How is articles by this outlet structured (inverted pyramid, narrative, chronological, etc.)?' },
      { key: 'breaking_v_reporting_differences', label: '(B) Breaking v Reporting Differences', description: 'Are there differences in structure for breaking news versus regular reporting?' },
      { key: 'ending_conventions', label: '(C) Ending Conventions', description: 'How does outlet typically end articles (open-ended, forward-looking, contextual wrap up, etc.)?' },
      { key: 'length', label: '(D) Length', description: 'How many paragraphs is a typical article? How many words?' },
    ],
  },
  {
    value: 'voice_posture',
    label: 'Voice & Posture',
    noteKey: 'voice_editorial_posture',
    fields: [
      { key: 'distance', label: '(A) Distance', description: 'How does writer present their distance from the subject (neutral observer, skeptical interrogator, institutional explainer, etc.)?' },
      { key: 'implicit_v_explicit_judgement', label: '(B) Implicit v Explicit Judgement', description: 'Does outlet appear to primarily use implicit or explicit judgement?' },
      { key: 'adjective_tolerance', label: '(C) Adjective Tolerance', description: 'How frequently does outlet use adjectives or adverbs in their reporting?' },
      { key: 'emotional_register', label: '(D) Emotional Register', description: 'How frequently does outlet use emotional or inflammatory language in their writing?' },
      { key: 'rhetorical_restraint', label: '(E) Rhetorical Restraint', description: 'Does outlet demonstrate rhetorical restraint?' },
      { key: 'contrast_and_irony', label: '(F) Contrast & Irony', description: 'Does outlet frequently use contrast or irony in their writing?' },
    ],
  },
  {
    value: 'evidentiary_standards',
    label: 'Evidentiary Standards',
    noteKey: 'evidentiary_standards_source_authority',
    fields: [
      { key: 'sources', label: '(A) Sources', description: 'Who does outlet consider to be primary authority sources? Secondary contextual sources? Credible modifiers?' },
      { key: 'source_count', label: '(B) Source Count', description: 'How many sources does outlet typically use for different types of stories?' },
      { key: 'document_v_interview', label: '(C) Document v Interview', description: 'How does outlet prioritize or use document based sources versus interviews with individuals?' },
      { key: 'original_sources', label: '(D) Original Sources', description: 'How often does outlet rely on information they themselves sourced (via interview or data collection) versus reporting on information provided by other news organizations?' },
      { key: 'anonymity', label: '(E) Anonymity', description: 'Does outlet use or avoid anonymous sources? Under what context do they use anonymous sources?' },
      { key: 'fact_checking', label: '(F) Fact-Checking', description: 'How does use and present fact-checking' },
    ],
  },
  {
    value: 'quotation_strategy',
    label: 'Quotation Strategy',
    noteKey: 'quotation_strategy',
    fields: [
      { key: 'quote_use', label: '(A) Quote Use', description: 'How frequently does outlet use direct quotes in typical articles?' },
      { key: 'quote_placement', label: '(B) Quote Placement', description: 'Where is article does outlet typically place quotes?' },
      { key: 'quote_length', label: '(C) Quote Length', description: 'How long are quotes?' },
      { key: 'attribution_language', label: '(D) Attribution Language', description: 'What language does outlet typically use to attribute quotes?' },
      { key: 'balance_expectations', label: '(E) Balance Expectations', description: 'Does outlet typically prioritize quotes from only one perspective, or do they always provide a quote from an opposing perspective?' },
    ],
  },
  {
    value: 'language_lexical',
    label: 'Language & Lexical',
    noteKey: 'language_lexical_conventions',
    fields: [
      { key: 'preferred_phrases', label: '(A) Preferred Phrases', description: 'Are there any specific phrases outlet prefers to use?' },
      { key: 'specific_terminology', label: '(B) Specific Terminology', description: 'Is there any specific terminology or labels outlet uses for key events, people, or places?' },
    ],
  },
  {
    value: 'context_discipline',
    label: 'Context Discipline',
    noteKey: 'context_discipline',
    fields: [
      { key: 'history_introduction', label: '(A) History Introduction', description: 'How does the outlet, if at all, introduce historical context in their articles?' },
      { key: 'context_introduction', label: '(B) Context Introduction', description: 'How does the outlet introduce broader context (economic conditions, peers, political constraints)?' },
      { key: 'lookback_norms', label: '(C) Lookback Norms', description: 'What is the typical lookback horizon norms, do they focus on very recent past or go back several years?' },
      { key: 'summarization_style', label: '(D) Summarization Style', description: 'How does author summarize context or history (mini-timeline, one-sentence compression, avoid detailed chronologies)?' },
      { key: 'comparison_logic', label: '(E) Comparison Logic', description: 'Does the outlet frequently employ comparison to contextualize articles (peer comparisons, past incidents?)' },
    ],
  },
  {
    value: 'legal_ethical',
    label: 'Legal & Ethical',
    noteKey: 'legal_ethical_guardrails',
    fields: [
      { key: 'allegation_framing', label: '(A) Allegation Framing', description: 'How does outlet frame allegations made against individuals or organizations?' },
      { key: 'right_of_reply', label: '(B) Right-of-Reply', description: 'Does outlet allow individuals or entities to respond to criticism?' },
      { key: 'right_of_reply_language', label: '(C) Right-of-Reply Language', description: 'What language do they use when offering entities the right to reply?' },
      { key: 'common_disclaimers', label: '(D) Common Disclaimers', description: 'What disclaimers does outlet use and under what conditions?' },
    ],
  },
  {
    value: 'headline_mechanics',
    label: 'Headline Mechanics',
    noteKey: 'headline_mechanics',
    fields: [
      { key: 'headline_formula', label: '(A) Headline Formula', description: 'What ways does outlet usually structure headlines?' },
      { key: 'headline_tense', label: '(B) Headline Tense', description: 'What verb tense are headlines typically written in?' },
      { key: 'punctuation_use', label: '(C) Punctuation Use', description: 'Do headlines make frequent use of colons, dashes, or question marks?' },
      { key: 'framing_constraints', label: '(D) Framing Constraints', description: 'Are headlines used to suggest the framing or perspective presented in the article?' },
      { key: 'assertion_v_uncertainty', label: '(E) Assertion v Uncertainty', description: 'Do headlines typically assert something as objectively true or prioritize uncertainty or debate?' },
      { key: 'clickbait', label: '(F) Clickbait', description: 'Do headlines use sensationalized, misleading, or exaggerated language?' },
    ],
  },
];

function parseCategoryNotes(raw: Record<string, string | null> | undefined): Record<string, string | null> {
  if (!raw) return {};
  // If the only key is raw_text and it contains code-fenced JSON, parse it
  if (raw.raw_text && Object.keys(raw).length === 1) {
    let text = raw.raw_text.trim();
    if (text.startsWith('```')) {
      const firstNl = text.indexOf('\n');
      if (firstNl !== -1) text = text.slice(firstNl + 1);
      if (text.trimEnd().endsWith('```')) text = text.trimEnd().slice(0, -3).trimEnd();
    }
    try {
      const parsed = JSON.parse(text);
      if (typeof parsed === 'object' && parsed !== null) return parsed;
    } catch {
      // Not valid JSON, return as-is
    }
  }
  return raw;
}

export function ReflectionCategoryTabs({ notes }: ReflectionCategoryTabsProps) {
  return (
    <Tabs defaultValue="newsworthiness">
      <Tabs.List>
        {CATEGORIES.map((cat) => (
          <Tabs.Trigger key={cat.value} value={cat.value}>
            {cat.label}
          </Tabs.Trigger>
        ))}
      </Tabs.List>

      {CATEGORIES.map((cat) => {
        const rawCategoryNotes = notes[cat.noteKey] as Record<string, string | null> | undefined;
        const categoryNotes = parseCategoryNotes(rawCategoryNotes);
        const hasFields = cat.fields.some((f) => !!categoryNotes?.[f.key]);

        return (
          <Tabs.Content key={cat.value} value={cat.value}>
            <Stack gap="md" style={{ paddingTop: 16 }}>
              {hasFields ? (
                cat.fields.map((field) => (
                  <ReflectionFieldCard
                    key={field.key}
                    label={field.label}
                    description={field.description}
                    value={categoryNotes?.[field.key] ?? null}
                  />
                ))
              ) : (
                <p style={{ fontStyle: 'italic', opacity: 0.7 }}>
                  Not enough article data was available to assess this category.
                </p>
              )}
            </Stack>
          </Tabs.Content>
        );
      })}
    </Tabs>
  );
}
